import React from "react";
import { Avatar, Badge, IconButton } from "@mui/material";
import {
  PhotoCamera as CameraIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { User } from "../../types";

interface UserAvatarProps {
  user: User;
  size?: number;
  showEditButton?: boolean;
  onEditClick?: () => void;
  onClick?: () => void;
  avatarFile?: File | null;
}

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    width: 24,
    height: 24,
    borderRadius: "50%",
    border: `2px solid ${theme.palette.background.paper}`,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  size = 40,
  showEditButton = false,
  onEditClick,
  onClick,
  avatarFile,
}) => {
  const getInitials = (firstName: string, lastName: string): string => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getAvatarSrc = (): string | undefined => {
    if (avatarFile) {
      return URL.createObjectURL(avatarFile);
    }
    return user.avatar;
  };

  const avatarContent = () => {
    const src = getAvatarSrc();

    if (src) {
      return (
        <Avatar
          src={src}
          alt={`${user.firstName} ${user.lastName}`}
          sx={{
            width: size,
            height: size,
            fontSize: size * 0.4,
            cursor: onClick ? "pointer" : "default",
          }}
          onClick={onClick}
        />
      );
    }

    return (
      <Avatar
        sx={{
          width: size,
          height: size,
          fontSize: size * 0.4,
          bgcolor: "primary.main",
          cursor: onClick ? "pointer" : "default",
        }}
        onClick={onClick}
      >
        {user.firstName && user.lastName ? (
          getInitials(user.firstName, user.lastName)
        ) : (
          <PersonIcon fontSize={size > 60 ? "large" : "medium"} />
        )}
      </Avatar>
    );
  };

  if (showEditButton && onEditClick) {
    return (
      <StyledBadge
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        badgeContent={
          <IconButton
            size="small"
            onClick={onEditClick}
            sx={{
              padding: 0,
              width: 24,
              height: 24,
              minWidth: 24,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              "&:hover": {
                bgcolor: "primary.dark",
              },
            }}
          >
            <CameraIcon fontSize="small" />
          </IconButton>
        }
      >
        {avatarContent()}
      </StyledBadge>
    );
  }

  return avatarContent();
};

export default UserAvatar;
